import React, {forwardRef, memo, useEffect, useImperativeHandle, useState} from "react";
import {Box} from "@mui/system";
import {
	Alert, Button,
	CircularProgress,
	IconButton,
	MenuItem,
	Select, Tooltip,
	Typography,
} from "@mui/material";
import {
	ArrowCircleLeft,
	ArrowCircleRight, CloudDownload, Download, SubdirectoryArrowRight,
} from "@mui/icons-material";
import ReactPanZoom from "react-image-pan-zoom-rotate";
import {HtmlRenderer} from "./HtmlRenderer";
import {createRoot} from "react-dom/client";
import {PdfRenderer} from "./PdfRenderer";
import {translateFunction} from "../../../../localization/TranslationService";
import {TextRenderer} from "./TextRenderer";


const translate = translateFunction("SureBaseDocuments");
const documentViewerPageIdentifierContainerStyling: React.CSSProperties = {
	display: "flex",
	justifyContent: "flex-end",
	flexDirection: "row",
	alignItems: "center",
	flexShrink: 1
};
const documentViewerPageIdentifierContainerStylingLeft: React.CSSProperties = {
	display: "flex",
	width: "100%",
	flexShrink: 1,
	justifyContent: "flex-start",
	flexDirection: "column",
	alignItems: "left",
};
const documentViewerPageIdentifierContainerStylingRight: React.CSSProperties = {
	display: "flex",
	width: "100%",
	flexGrow: 1,
	justifyContent: "flex-end",
	flexDirection: "row",
	alignItems: "center",
};


interface IDocumentViewerCustomRenderer {
	extensions: string[];
	getRenderer: (presignedUrl: string) => React.ReactElement;
	
}

export interface IDocumentViewerFile {
	id: number;
	name: string;
	extension: string;
	presignedUrl?: string;
	getPresignedUrl?: () => Promise<{url: string; fileName: string}>;
	groupId: number;
	parentGroupId: number;
}

export interface IDocumentViewerProps {
	documentId: number;
	defaultDocumentId?: number;
	sourceDocument?: IDocumentViewerFile;
	documents: IDocumentViewerFile[];
	groupId: number;
	customRenderers?: IDocumentViewerCustomRenderer[];
	children?: React.ReactNode;
	onSelectedFileChanged?: (currentFile: number) => void;
}

export interface IDocumentViewerHandles {
	selectDocument: (newDocumentId: number) => void;
	nextDocument: () => void;
}

export const DocumentViewer = memo(forwardRef<IDocumentViewerHandles, IDocumentViewerProps>((props, ref) => {
	const [documents] = useState<IDocumentViewerFile[]>(props.documents);
	const [selectedDocument, setSelectedDocument] = useState<IDocumentViewerFile>(
		documents.filter(it => it.id === props.defaultDocumentId).first()
	);
	
	const containerId = "document-viewer-react-container-" + props.groupId;
	
	const defaultRenderers: IDocumentViewerCustomRenderer[] = [
		{
			// This Renderer will be used for all unknown extensions
			extensions: [""],
			getRenderer: (_) => {
				return <div style={{height: "auto"}}><Alert
					severity={"warning"}>{translate("This type of document cannot be viewed here, please download the document and open from the download.")}<Button
						onClick={onDownload}>
						<Download/>
					</Button></Alert></div>;
			},
		},
		{
			extensions: [".jpg", ".png", ".jpeg", "tiff", ".tif",".heic"],
			getRenderer: (presignedUrl) => {
				return (
					<div
						style={{
							flexGrow: 1,
							maxHeight: "auto",
							height: "auto",
							width: "auto",
							position: "relative",
							overflow: "scroll",
						}}
					>
						<ReactPanZoom image={presignedUrl} alt="Image alt text"/>
					</div>
				);
			},
		},
		{
			extensions: [".html"],
			getRenderer: (presignedUrl) => {
				return (
					<div
						style={{
							maxHeight: "100%",
							height: "100%",
							width: "100%",
							position: "relative",
							overflow: "scroll",
						}}
					>
						<HtmlRenderer presignedUrl={presignedUrl}/>
					</div>
				);
			},
		},
		{
			extensions: [".txt"],
			getRenderer: (presignedUrl) => {
				return <TextRenderer presignedUrl={presignedUrl}/>;
			},
		},
		{
			extensions: [".msg", ".eml"],
			getRenderer: (presignedUrl) => {
				return <TextRenderer presignedUrl={presignedUrl}/>;
			},
		},
		{
			extensions: [".pdf"],
			getRenderer: (presignedUrl) => {
				return <PdfRenderer presignedUrl={presignedUrl}/>;
			},
		},
	
	];
	
	const selectCurrentFile = (newDocumentId: number) => {
		console.log(newDocumentId);
		const newDocument = documents.find(
			(it) => it.id === newDocumentId
		);
		if (newDocument) {
			setSelectedDocument(newDocument);
		}
	};
	const nextDocument = () => {
		const currentIndex = documents.findIndex(
			(it) => it.id === selectedDocument?.id
		);
		if (currentIndex === documents.length - 1) {
			setSelectedDocument(documents[0]);
			return;
		}
		
		setSelectedDocument(documents[currentIndex + 1]);
	};
	
	const previousDocument = () => {
		const currentIndex = documents.findIndex(
			(it) => it.id === selectedDocument?.id
		);
		
		if (currentIndex === 0) {
			setSelectedDocument(documents[documents.length - 1]);
			return;
		}
		
		setSelectedDocument(documents[currentIndex - 1]);
	};
	
	const getPresignedUrl = async (): Promise<{url: string; fileName: string}> => {
		if (selectedDocument.presignedUrl) {
			return {url: selectedDocument.presignedUrl, fileName: selectedDocument.name};
		} else if (selectedDocument.getPresignedUrl) {
			return await selectedDocument.getPresignedUrl();
		}

		throw new Error("No presigned url found");
	};
	
	const getPresignedUrlSourceDocument = async (): Promise<{url: string; fileName: string}> => {
		if (props.sourceDocument?.presignedUrl) {
			return {url: props.sourceDocument.presignedUrl, fileName: props.sourceDocument.name};
		} else if (props.sourceDocument?.getPresignedUrl) {
			return await props.sourceDocument.getPresignedUrl();
		}

		throw new Error("No presigned url found");
	};
	const renderer = (selectedDocument: IDocumentViewerFile) => {
		const container = document.getElementById(
			containerId
		);
		const loader = document.getElementById("document-viewer-react-overlay-box");
		if (loader) loader.style.visibility = "visible";
		
		if (selectedDocument) {
			
			let renderer: React.ReactElement;
			getPresignedUrl()
				.then((presignedUrlResult) => {
					const appropriateRenderer = [
						...(props.customRenderers ?? []),
						...defaultRenderers,
					]
						.filter((renderer) =>
							renderer.extensions.includes(
								selectedDocument.extension.toLowerCase()
							)
						)
						.first();
					
					// if no renderer is found for the extension, use a default one
					if (!appropriateRenderer) {
						console.warn(
							`No renderer found for the extension ${selectedDocument.extension}. Falling back to the first default renderer.`
						);
						renderer = defaultRenderers[0].getRenderer(presignedUrlResult.url);
					} else {
						renderer = appropriateRenderer.getRenderer(presignedUrlResult.url);
					}
				})
				.finally(() => {
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					// ReactDOM.render(renderer, container);
					const reactContainer = createRoot(container as Element);
					reactContainer.render(renderer);
					if (loader) loader.style.visibility = "hidden";
				});
		}
	};
	
	const onDownload = () => {
		getPresignedUrl()
			.then((presignedUrlResult) => {
				fetch(presignedUrlResult.url).then((response) => {
					response.blob().then((blob) => {
						const fileURL =
							window.URL.createObjectURL(blob);

						const alink = document.createElement("a");
						alink.href = fileURL;
						alink.download = presignedUrlResult.fileName;
						alink.click();
					});
				});

			});

	};
	
	const onDownloadSourceDocument = () => {
		getPresignedUrlSourceDocument()
			.then((presignedUrlResult) => {
				fetch(presignedUrlResult.url).then((response) => {
					response.blob().then((blob) => {
						const fileURL =
							window.URL.createObjectURL(blob);

						const alink = document.createElement("a");
						alink.href = fileURL;
						alink.download = presignedUrlResult.fileName;
						alink.click();
					});
				});

			});

	};
	

	useImperativeHandle(ref, () => ({
		// This method can now be called from the parent
		selectDocument: (newDocumentId: number) => selectCurrentFile(newDocumentId),
		nextDocument: () => nextDocument(),
	}));
	
	useEffect(() => {
		props.onSelectedFileChanged && props.onSelectedFileChanged(selectedDocument?.id);
		renderer(selectedDocument);
	});
	
	if (documents.length < 1) {
		return <Alert severity={"warning"}>{translate("No documents found")}</Alert>;
	}
	
	return (
		<>
			<div
				style={{
					height: "100%",
					maxHeight: "100%",
					display: "flex",
					flexDirection: "column",
				}}
			
			>
				<Box sx={documentViewerPageIdentifierContainerStyling}>
					<Box sx={documentViewerPageIdentifierContainerStylingRight}>
						<Select value={selectedDocument.id}>
							{documents.map((it) => {
								return (
									<MenuItem key={it.id} value={it.id} onClick={() => setSelectedDocument(it)}>
										{it.parentGroupId !== null && (it.parentGroupId !== 0) &&
											<SubdirectoryArrowRight/>}
										{it.name}
									</MenuItem>
								);
							})}
						</Select>
						<IconButton onClick={previousDocument}>
							<ArrowCircleLeft/>
						</IconButton>
						<Typography variant="h6" component="div">
							Document{" "}
							{documents.findIndex((it) => it.id === selectedDocument?.id) + 1}/
							{documents.length}
						</Typography>
						<IconButton onClick={nextDocument}>
							<ArrowCircleRight/>
						</IconButton>
						<Tooltip title={translate("Download File")}>
							<IconButton onClick={onDownload}>
								<Download/>
							</IconButton>
						</Tooltip>
						{props.sourceDocument &&
							<Tooltip title={translate("Download Original")}>
								<IconButton onClick={onDownloadSourceDocument}>
									<CloudDownload/>
								</IconButton>
							</Tooltip>
						}
					</Box>
				
				</Box>
				<Box sx={documentViewerPageIdentifierContainerStylingLeft}>
					
					{props.children}
				</Box>
				
				<div
					id={containerId}
					key={selectedDocument?.id}
					style={{display: "flex", flexGrow: 2, overflow: "hidden"}}
				></div>
			</div>
			<Box
				id="document-viewer-react-overlay-box"
				style={{
					position: "absolute",
					top: 0,
					left: 0,
					width: "100%",
					height: "100%",
					backgroundColor: "rgba(202, 202, 202, 0.95)",
					display: "flex",
					zIndex: 3,
				}}
			>
				<CircularProgress style={{margin: "auto"}} color="success"/>
			</Box>
		</>
	);
}));
